import React, { useCallback, useContext, useState } from "react";
import { SubscriptionDetailContext } from "../../../context/SubscriptionDetailContext";
import { FormattedMessage, injectIntl } from "react-intl";
import { messages } from "../messages";
import { Button, Title, useToggleState } from "@yoast/ui-library";
import { PlusIcon } from "@heroicons/react/20/solid";
import * as styles from "./styles.module.scss";
import { AddSiteModal } from "../../modal/AddSiteModal";
import { IconButton } from "../../IconButton";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import ConnectSiteToSubscription from "../../ConnectSiteToSubscription/ConnectSiteToSubscription";

/**
 * A list of connected sites in the subscription.
 * @returns {Element} The connected sites.
 */
export const ConnectedSites = injectIntl( ( { intl } ) => {
	const { subscription, request }                                                 = useContext( SubscriptionDetailContext );
	const [ addSiteModalOpen, , , openAddSiteModal, closeAddSiteModal ]             = useToggleState( false );
	const [ isRemovingSiteForSubscription, setIsRemovingSiteForSubscription ]       = useState( false );
	const [ removingSiteForSubscriptionError, setRemovingSiteForSubscriptionError ] = useState( null );


	const removeSite = useCallback( async( siteId ) => {
		setIsRemovingSiteForSubscription( true );
		setRemovingSiteForSubscriptionError( null );

		try {
			/**
			 * @type {import("../../../../../nestjs-server/src/controllers/entities/site/dtos/RemoveSubscriptionDto")
			 * .RemoveSubscriptionResponseDto}
			 */
			const response = await doRequest( prepareInternalRequest(
				`Sites/${ siteId }/subscriptions/${ subscription.id }`,
				"DELETE",
			) );

			if ( ! response.success ) {
				setRemovingSiteForSubscriptionError( {
					type: "error",
					message: intl.formatMessage( messages.unlinkAccountFailed ),
				} );
				return;
			}

			request.reload();
		} catch ( error ) {
			setRemovingSiteForSubscriptionError( {
				type: "error",
				message: intl.formatMessage( messages.unlinkAccountFailed ),
			} );
		} finally {
			setIsRemovingSiteForSubscription( false );
		}
	}, [ subscription, setIsRemovingSiteForSubscription, setRemovingSiteForSubscriptionError ] );

	if ( request.isFirstLoad || ! subscription ) {
		return <></>;
	}

	return <section className={ styles.connectedSites }>
		<Title as={ "p" } size={ "5" }>
			<FormattedMessage
				{ ...messages.connectedSitesTitle }
				values={ { used: subscription.connectedSites.length, limit: subscription.limit } }
			/>
		</Title>
		<ul>
			{ subscription.connectedSites?.map( site => {
				return <li key={ site.id }>
					<div>
						<span className={ "yst-text-slate-600" }>{ site.url }</span>
						{ subscription.allowSiteActions && <Button
							className={ styles.removeButton } variant={ "tertiary" } size="small"
							onClick={ () => removeSite( site.id ) }
							isLoading={ isRemovingSiteForSubscription }
							disabled={ isRemovingSiteForSubscription }
						>
							<FormattedMessage { ...messages.removeSite } />
						</Button> }
						{ removingSiteForSubscriptionError &&
							<span><FormattedMessage { ...messages.removeSiteError } /></span> }
					</div>
				</li>;
			} ) }
			{ subscription.allowSiteActions && <li>
				<IconButton
					className={ styles.addSiteButton }
					variant="tertiary"
					size="small"
					onClick={ openAddSiteModal }
					disabled={ subscription.connectedSites?.length >= subscription.limit }
					data-testid="add-site-button"
				>
					<PlusIcon />
					<FormattedMessage { ...messages.connectSite } />
				</IconButton>
			</li> }
		</ul>

		<AddSiteModal onClose={ closeAddSiteModal } modalOpen={ addSiteModalOpen }>
			<ConnectSiteToSubscription onSuccess={ closeAddSiteModal } />
		</AddSiteModal>
	</section>;
} );
